import { useRef, useState, useSyncExternalStore } from 'react';
import { CheckCircle2, Info, LoaderCircle, ShieldAlert, X } from 'lucide-react';
import { actionFeedback, reportActionError, type FeedbackMessage } from './action-feedback-store';
import './action-feedback.css';
import { createSubmissionGate } from './submission-gate';

export function ActionFeedbackItem({ message }: { message: FeedbackMessage }) {
  const Icon = message.state === 'pending' ? LoaderCircle : message.state === 'success' ? CheckCircle2 : message.state === 'error' ? ShieldAlert : Info;
  return <article className={`action-feedback-item is-${message.state}`} role={message.state === 'error' ? 'alert' : 'status'} aria-atomic="true">
    <Icon aria-hidden="true" className={message.state === 'pending' ? 'spin' : ''} />
    <div><strong>{message.title}</strong>{message.detail && <p>{message.detail}</p>}</div>
    <button type="button" className="action-feedback-dismiss" aria-label="Đóng thông báo thao tác" onClick={() => actionFeedback.dismiss(message.id)}><X aria-hidden="true" /></button>
  </article>;
}
export function ActionFeedbackToasts() {
  const messages = useSyncExternalStore(actionFeedback.subscribe, actionFeedback.getSnapshot, actionFeedback.getSnapshot);
  return <aside className="action-feedback-stack" aria-label="Phản hồi thao tác">{messages.map(message => <ActionFeedbackItem key={message.id} message={message} />)}</aside>;
}

// Lock synchronously (before React rerenders) and contain rejected event handlers.
// This changes only UI submissions, never retries/coalesces backend requests.
export function useActionGate() {
  const [busy, setBusy] = useState(false);
  const lock = useRef<ReturnType<typeof createSubmissionGate> | null>(null);
  if (!lock.current) lock.current = createSubmissionGate(setBusy);
  const guard = <Args extends unknown[], T,>(operation: (...args: Args) => Promise<T>) => async (...args: Args): Promise<T | undefined> => {
    // Some handlers take (id, event); prevent native form submission even when
    // a concurrent invocation is ignored by the lock.
    for (const arg of args) {
      const event = arg as { preventDefault?: () => void } | undefined;
      if (typeof event?.preventDefault === 'function') event.preventDefault();
    }
    return lock.current!(async () => {
      try { return await operation(...args); }
      catch (error) { reportActionError(error); return undefined; }
    });
  };
  return { busy, guard };
}
